/**
 * Messaging providers (`docs/31_SMS_PROVIDERS.md`): the accounts Connect holds
 * at its vendors — the SMS reseller account — and everything done at them.
 *
 * What the vendor's API does, Connect does, through the connection's own
 * actions (read the balance, sync senders and templates). What only the
 * vendor's panel does — create or load a client's account, change its rate,
 * suspend it, approve its templates — a person does there, following the
 * steps the catalog declares, and records here. Recording never calls the
 * vendor: the server refuses to "record" an API operation, and a panel one is
 * only ever what somebody says they did, kept in an append-only log.
 *
 * Every figure, rule and refusal is the server's: the cover, which services a
 * member of staff may see, whether a load belongs on a credit purchase.
 */

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import {
  Alert,
  ApiError,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  DetailField,
  DetailGrid,
  Drawer,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  PageHeader,
  ScrollArea,
  Sections,
  Select,
  StatCard,
  StatRow,
  StatusChip,
  api,
  formatDateTime,
  formatNumber,
  toast,
  useCan,
} from '@connect/web-shared';

import { formatMetered } from './ai-card';
import { refusalText } from './api-refusal';
import {
  AlertsList,
  AllocationQueue,
  AllocationsCard,
  EntriesCard,
  type AccountEntry,
  type Allocation,
  type ProviderAlertRow,
} from './provider-allocations';
import { ProviderFields, fieldsValid, initialValues, requiredFilled, type FieldValues } from './provider-fields';
import type { FieldSpec } from './types';

// ── what the API says ───────────────────────────────────────
export interface Operation {
  id: string;
  label: string;
  scope: 'account' | 'connection';
  method: 'api' | 'panel';
  description: string;
  action: 'verify' | 'sync_senders' | 'sync_templates' | 'account_load' | 'account_balance' | null;
  panel_path: string | null;
  steps: string[];
  record: FieldSpec[];
  effect: 'record' | 'reading' | 'cost' | 'profile' | 'link' | 'load' | 'reload' | 'refund' | 'adjustment';
  sets: Record<string, string>;
}

interface Account {
  public_id: string;
  provider: string;
  provider_label: string;
  label: string;
  account_ref: string;
  panel_url: string | null;
  currency: string;
  unit_cost: string | null;
  note: string | null;
  is_active: boolean;
  low_balance_threshold: string | null;
  /** Whether Connect loads clients' accounts itself (`api`) or staff do (`panel`). */
  allocates: 'api' | 'panel';
  /** The login a provider with an account API needs; empty for one without. */
  account_credentials: FieldSpec[];
  /** Which of them are stored — never their values. */
  credentials: Record<string, { set: boolean }>;
  balance: string | null;
  balance_at: string | null;
}

interface ProviderChoice {
  id: string;
  label: string;
  account_label: string | null;
  account_credentials: FieldSpec[];
}

interface Connection {
  public_id: string;
  client: { public_id: string; name: string };
  label: string | null;
  status: string;
  is_primary: boolean;
  billing_mode: 'client_pays_vendor' | 'credits';
  customer_ref: string | null;
  profile: Record<string, string>;
}

interface LinkedConnection extends Connection {
  balance: string | null;
  balance_at: string | null;
  parts: number | null;
  open_purchases: number;
}

interface LogEntry {
  public_id: string;
  operation: string;
  label: string;
  method: 'api' | 'panel';
  client: string | null;
  values: Record<string, string>;
  result: Record<string, string | null>;
  note: string | null;
  by: string;
  at: string;
}

/**
 * The account's balance as Connect works it out since its last real reading:
 * plus the vendor's reloads, refunds and adjustments since, less every part its
 * clients sent since at its cost. A new reading resets it.
 */
interface BalanceEstimate {
  balance: string;
  reading: string;
  reading_at: string;
  sent_parts: number;
  sent_cost: string;
  movements: string;
  loads: string;
}

interface AccountDetail {
  account: Account;
  readings: { balance: string; currency: string; at: string }[];
  estimate: BalanceEstimate | null;
  /**
   * What sending every client's balance would take from the account (each part
   * a client sends is paid from it, at its cost), against what it holds.
   */
  cover: {
    state: 'covered' | 'short' | 'unknown';
    balance: string | null;
    client_cost: string | null;
    /** How much reseller balance to buy for it to cover them, when short. */
    to_buy: string | null;
    unknown: number;
  };
  connections: LinkedConnection[];
  unlinked: Connection[];
  operations: Operation[];
  vendor_profile: FieldSpec[];
  log: LogEntry[];
  allocations: Allocation[];
  alerts: ProviderAlertRow[];
  entries: AccountEntry[];
  /** What the account can still give loads: its estimated balance, less loads on their way. */
  available: string | null;
}

/** How the estimate was reached, in one line under it. */
function EstimateHint({ estimate }: { estimate: BalanceEstimate }) {
  const moved = estimate.movements !== '0.0000' ? `, ${formatMetered(estimate.movements)} refunds/reloads` : '';
  const loads = estimate.loads !== '0.0000' ? `, ${formatMetered(estimate.loads)} loads` : '';
  return (
    <span>
      {`${formatMetered(estimate.reading)} read ${formatDateTime(estimate.reading_at)}, less ${formatNumber(estimate.sent_parts)} part(s) sent since (${formatMetered(estimate.sent_cost)})${moved}${loads}.`}
    </span>
  );
}

const keys = {
  all: ['provider-accounts'] as const,
  list: () => ['provider-accounts', 'list'] as const,
  one: (id: string) => ['provider-accounts', id] as const,
};

const COVER_LABELS = { covered: 'Covered', short: 'Short', unknown: 'Not known' };
const COVER_TONES = { covered: 'success', short: 'danger', unknown: 'neutral' } as const;
const ACTIVE_LABELS = { yes: 'Active', no: 'Suspended' };
const ACTIVE_TONES = { yes: 'success', no: 'warning' } as const;
const MODE_LABELS = { client_pays_vendor: 'Client pays the vendor', credits: 'Sold in credits' };

/** A step with the client's own values in it. */
export function fillStep(step: string, client: string | null, customer: string | null): string {
  return step.replaceAll('{client}', client ?? 'the client').replaceAll('{customer}', customer ?? '—');
}

function Table({ children }: { children: React.ReactNode }) {
  return (
    <ScrollArea className="">
      <table className="w-full text-sm">{children}</table>
    </ScrollArea>
  );
}

function Th({ children, right }: { children?: React.ReactNode; right?: boolean }) {
  return (
    <th className={`px-3 py-2 text-2xs font-medium uppercase tracking-wide text-faint ${right ? 'text-right' : 'text-left'}`}>
      {children}
    </th>
  );
}

// ── the list ────────────────────────────────────────────────
export function ProvidersPage() {
  const navigate = useNavigate();
  const canCreate = useCan('messaging.provider_account.create');
  const [adding, setAdding] = useState(false);
  const list = useQuery({
    queryKey: keys.list(),
    queryFn: () =>
      api.get<{
        accounts: (Account & { linked: number })[];
        providers: ProviderChoice[];
      }>('/messaging/provider-accounts'),
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Messaging providers"
        description="The accounts Connect holds at its vendors, and every client's account under them. What a vendor's API does, Connect does; what only its panel does, the steps are here and the result is recorded."
        actions={
          canCreate && list.data?.providers.length ? (
            <Button onClick={() => setAdding(true)}>Add an account</Button>
          ) : null
        }
      />
      {list.isLoading ? (
        <LoadingState label="Loading provider accounts" />
      ) : list.isError || !list.data ? (
        <ErrorState onRetry={() => void list.refetch()} />
      ) : list.data.accounts.length === 0 ? (
        <Card>
          <EmptyState
            title="No provider accounts yet"
            description="Add the reseller account Connect holds at the SMS vendor. Its clients' accounts are then linked to it."
          />
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <thead className="bg-surface-2">
              <tr>
                <Th>Account</Th>
                <Th>Provider</Th>
                <Th right>Balance (recorded)</Th>
                <Th right>Cost per part</Th>
                <Th right>Clients</Th>
              </tr>
            </thead>
            <tbody>
              {list.data.accounts.map((account) => (
                <tr
                  key={account.public_id}
                  className="cursor-pointer border-t border-line hover:bg-surface-2"
                  onClick={() => navigate(`/messaging/providers/${account.public_id}`)}
                >
                  <td className="px-3 py-2">
                    <span className="font-medium">{account.label}</span>
                    {!account.is_active ? <Badge className="ml-2">Off</Badge> : null}
                  </td>
                  <td className="px-3 py-2 text-muted">{account.provider_label}</td>
                  <td className="px-3 py-2 text-right tabular">
                    {account.balance ? formatMetered(account.balance) : '—'}
                    {account.balance_at ? (
                      <div className="text-2xs text-faint">{formatDateTime(account.balance_at)}</div>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-right tabular">
                    {account.unit_cost ? formatMetered(account.unit_cost) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right tabular">{formatNumber(account.linked)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}
      {list.data ? (
        <AllocationQueue
          panelUrls={Object.fromEntries(list.data.accounts.map((a) => [a.public_id, a.panel_url]))}
        />
      ) : null}
      {adding && list.data ? (
        <AddAccountDrawer providers={list.data.providers} onClose={() => setAdding(false)} />
      ) : null}
    </div>
  );
}

function AddAccountDrawer({
  providers,
  onClose,
}: {
  providers: ProviderChoice[];
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    provider: providers[0]?.id ?? '',
    label: '',
    account_ref: '',
    unit_cost: '',
    low_balance_threshold: '',
    note: '',
  });
  const chosen = providers.find((p) => p.id === form.provider);
  const specs = chosen?.account_credentials ?? [];
  const [credentials, setCredentials] = useState<FieldValues>({});
  const save = useMutation({
    mutationFn: () =>
      api.post<AccountDetail>('/messaging/provider-accounts', {
        ...form,
        unit_cost: form.unit_cost.trim() || null,
        low_balance_threshold: form.low_balance_threshold.trim() || null,
        note: form.note.trim() || null,
        credentials: specs.length ? credentials : null,
      }),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: keys.all });
      toast.success('Account added');
      navigate(`/messaging/providers/${data.account.public_id}`);
    },
    onError: (e) => toast.error('Not added', refusalText(e)),
  });
  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm({ ...form, [key]: e.target.value });
  const fieldErrors = save.error instanceof ApiError ? save.error.fieldErrors : undefined;

  return (
    <Drawer open onClose={onClose} title="Add a provider account">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <Select
          label="Provider"
          value={form.provider}
          onChange={set('provider')}
          options={providers.map((p) => ({ value: p.id, label: `${p.label} — ${p.account_label ?? ''}` }))}
        />
        <Input label="Name" required value={form.label} onChange={set('label')} hint="What staff call it." />
        <Input
          label="The vendor's reference for it"
          required
          value={form.account_ref}
          onChange={set('account_ref')}
          hint="Its account number or sign-in name at the vendor — never a password."
        />
        <Input
          label="Cost per SMS part (₹)"
          type="number"
          value={form.unit_cost}
          onChange={set('unit_cost')}
          hint="From the panel's SMS Pricing. It can be recorded later."
        />
        <Input
          label="Low-balance alert below (₹)"
          type="number"
          value={form.low_balance_threshold}
          onChange={set('low_balance_threshold')}
          hint="Staff are told when a reading is under it. Empty: never."
        />
        {specs.length ? (
          <ProviderFields
            specs={specs}
            values={credentials}
            onChange={setCredentials}
            errors={fieldErrors}
            errorPrefix="credentials"
          />
        ) : null}
        <Input label="Note" value={form.note} onChange={set('note')} />
        <Button
          type="submit"
          loading={save.isPending}
          disabled={
            !form.label.trim() || !form.account_ref.trim() || (specs.length > 0 && !requiredFilled(specs, credentials))
          }
        >
          Add the account
        </Button>
      </form>
    </Drawer>
  );
}

// ── one account ─────────────────────────────────────────────
export function ProviderAccountPage() {
  const { publicId = '' } = useParams<{ publicId: string }>();
  const detail = useQuery({
    queryKey: keys.one(publicId),
    queryFn: () => api.get<AccountDetail>(`/messaging/provider-accounts/${publicId}`),
  });
  const [doing, setDoing] = useState<{ op: Operation; connection: Connection | null } | null>(null);
  const [opened, setOpened] = useState<string | null>(null);

  if (detail.isLoading) return <LoadingState label="Loading the account" />;
  if (detail.isError || !detail.data) return <ErrorState onRetry={() => void detail.refetch()} />;
  const data = detail.data;
  const account = data.account;
  const accountOps = data.operations.filter((o) => o.scope === 'account');
  const panelUrls = { [account.public_id]: account.panel_url };
  const waiting = data.allocations.filter((a) => !['confirmed', 'cancelled'].includes(a.status));
  const connectionOps = data.operations.filter((o) => o.scope === 'connection');
  const connection = data.connections.find((c) => c.public_id === opened) ?? null;

  return (
    <div className="space-y-4">
      <PageHeader
        title={account.label}
        description={`${account.provider_label} · ${account.account_ref}`}
        breadcrumbs={[{ label: 'Messaging providers', to: '/messaging/providers' }, { label: account.label }]}
        actions={
          account.panel_url ? (
            <a
              href={account.panel_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-accent hover:underline"
            >
              Open the vendor's panel <ExternalLink className="size-3.5" />
            </a>
          ) : null
        }
      />
      <Sections
        items={[
          {
            id: 'overview',
            label: 'Overview',
            hint: 'Balance, cost and cover',
            content: <Overview data={data} ops={accountOps} onDo={(op) => setDoing({ op, connection: null })} />,
          },
          {
            id: 'clients',
            label: 'Clients',
            hint: 'Each client’s account under it',
            badge: <Badge>{data.connections.length}</Badge>,
            content: (
              <Clients
                data={data}
                linkOps={connectionOps.filter((o) => o.effect === 'link')}
                onOpen={setOpened}
                onDo={(op, c) => setDoing({ op, connection: c })}
              />
            ),
          },
          {
            id: 'purchases',
            label: 'Purchases',
            hint: 'Credits bought, loaded here',
            badge: waiting.length ? <Badge tone="warning">{waiting.length}</Badge> : undefined,
            content: (
              <AllocationsCard
                title="Clients' credit purchases"
                description={
                  account.allocates === 'api'
                    ? 'Connect loads each client account through the vendor’s API and adds the credits when the vendor confirms.'
                    : 'Each client account is loaded by hand in the vendor’s panel, then the load is recorded here.'
                }
                rows={data.allocations}
                panelUrls={panelUrls}
                empty="No credit purchase has been loaded through this account."
              />
            ),
          },
          {
            id: 'movements',
            label: 'Movements',
            hint: 'Reloads, refunds, adjustments',
            content: <EntriesCard entries={data.entries} />,
          },
          { id: 'log', label: 'Log', hint: 'What was done', content: <Log entries={data.log} /> },
        ]}
      />
      {connection ? (
        <ConnectionDrawer
          account={account}
          connection={connection}
          ops={connectionOps.filter((o) => o.effect !== 'link')}
          onClose={() => setOpened(null)}
          onDo={(op) => setDoing({ op, connection })}
        />
      ) : null}
      {doing ? (
        <OperationDrawer
          account={account}
          op={doing.op}
          connection={doing.connection}
          openPurchases={
            data.connections.find((c) => c.public_id === doing.connection?.public_id)?.open_purchases ?? 0
          }
          onClose={() => setDoing(null)}
        />
      ) : null}
    </div>
  );
}

function Overview({ data, ops, onDo }: { data: AccountDetail; ops: Operation[]; onDo: (op: Operation) => void }) {
  const { account, cover } = data;
  const canRecord = useCan('messaging.provider_account.create');
  const queryClient = useQueryClient();
  const readNow = useMutation({
    mutationFn: () => api.post<AccountDetail>(`/messaging/provider-accounts/${account.public_id}/read-balance`, {}),
    onSuccess: (fresh) => {
      queryClient.setQueryData(keys.one(account.public_id), fresh);
      toast.success('Balance read');
    },
    onError: (e) => toast.error('Not read', refusalText(e)),
  });
  return (
    <div className="space-y-4">
      <AlertsList alerts={data.alerts} accountId={account.public_id} />
      {cover.state === 'short' ? (
        <Alert
          tone="danger"
          title={`Clients' balances would cost the reseller ${formatMetered(cover.client_cost ?? '0')} to send; it holds about ${formatMetered(cover.balance ?? '0')}`}
        >
          Each part a client sends is paid from this account at its cost. Once it runs out, clients' sends fail at the
          vendor while their own balances still show credit.
          {cover.to_buy ? ` Buy at least ${formatMetered(cover.to_buy)} of reseller balance.` : ' Buy reseller balance.'}
        </Alert>
      ) : null}
      <StatRow className="xl:grid-cols-5">
        <StatCard
          label="Balance (estimated)"
          value={data.estimate ? formatMetered(data.estimate.balance) : 'Not known'}
          hint={data.estimate ? <EstimateHint estimate={data.estimate} /> : 'Needs a reading and the cost per part.'}
        />
        <StatCard
          label="Last reading"
          value={account.balance ? formatMetered(account.balance) : 'Not recorded'}
          hint={account.balance_at ? `Read ${formatDateTime(account.balance_at)}` : 'The vendor gives no API for it.'}
        />
        <StatCard
          label="Cost per SMS part"
          value={account.unit_cost ? formatMetered(account.unit_cost) : 'Not recorded'}
          hint="What the vendor takes from this account for each part any client sends."
        />
        <StatCard
          label="Clients' balances would cost it"
          value={cover.client_cost ? formatMetered(cover.client_cost) : '—'}
          tone={cover.state === 'short' ? 'danger' : 'neutral'}
          hint={
            <span className="inline-flex flex-wrap items-center gap-1">
              <StatusChip value={cover.state} labels={COVER_LABELS} tones={COVER_TONES} />
              {cover.unknown
                ? `${formatNumber(cover.unknown)} service(s) left out: balance or rate not known yet.`
                : 'Every client’s last balance, sent at this account’s cost.'}
            </span>
          }
        />
        <StatCard
          label="Available for new purchases"
          value={data.available ? formatMetered(data.available) : 'Not known'}
          hint="The estimated balance, less loads on their way. Clients’ balances only warn."
        />
      </StatRow>
      <Card>
        <CardHeader
          title="At the vendor"
          description="Done in the vendor's panel, then recorded here."
        />
        <CardBody className="space-y-2">
          {ops.map((op) => (
            <div key={op.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-line pb-2 last:border-0">
              <div className="min-w-0">
                <div className="text-sm font-medium">{op.label}</div>
                <div className="text-xs text-muted">{op.description}</div>
              </div>
              {!canRecord ? null : op.method === 'api' && op.action === 'account_balance' ? (
                <Button size="sm" variant="secondary" loading={readNow.isPending} onClick={() => readNow.mutate()}>
                  Read now
                </Button>
              ) : op.method === 'panel' ? (
                <Button size="sm" variant="secondary" onClick={() => onDo(op)}>
                  Steps and record
                </Button>
              ) : null}
            </div>
          ))}
        </CardBody>
      </Card>
      <AccountSettings account={account} />
      {account.account_credentials.length ? <AccountLogin account={account} /> : null}
      <Card>
        <CardHeader title="Balance readings" description="As recorded from the panel, newest first." />
        {data.readings.length === 0 ? (
          <CardBody>
            <p className="text-sm text-muted">None yet.</p>
          </CardBody>
        ) : (
          <Table>
            <tbody>
              {data.readings.map((r) => (
                <tr key={r.at} className="border-t border-line first:border-0">
                  <td className="px-3 py-2 text-xs text-muted">{formatDateTime(r.at)}</td>
                  <td className="px-3 py-2 text-right tabular">{formatMetered(r.balance)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}

/**
 * The login Connect uses to load clients' accounts by itself (`docs/31` §10).
 * Write-only: the server says which parts are stored, never what they are.
 */
function AccountLogin({ account }: { account: Account }) {
  const canEdit = useCan('messaging.provider_account.edit');
  const queryClient = useQueryClient();
  const [values, setValues] = useState<FieldValues>(() => initialValues(account.account_credentials));
  const save = useMutation({
    mutationFn: () =>
      api.patch<AccountDetail>(`/messaging/provider-accounts/${account.public_id}`, {
        credentials: Object.fromEntries(Object.entries(values).filter(([, v]) => v.trim() !== '')),
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(keys.one(account.public_id), data);
      setValues(initialValues(account.account_credentials));
      toast.success('Login saved');
    },
    onError: (e) => toast.error('Not saved', refusalText(e)),
  });
  const fieldErrors = save.error instanceof ApiError ? save.error.fieldErrors : undefined;
  return (
    <Card>
      <CardHeader
        title="Loading clients by itself"
        description={
          account.allocates === 'api'
            ? 'Connect loads each client’s account itself when a credit purchase needs it, and hands it to the queue when it cannot.'
            : 'With the reseller panel’s login stored, Connect loads clients’ accounts itself. Until then, staff load them by hand.'
        }
        actions={
          <StatusChip
            value={account.allocates}
            labels={{ api: 'Automatic', panel: 'By hand' }}
            tones={{ api: 'success', panel: 'neutral' }}
          />
        }
      />
      {canEdit ? (
        <CardBody className="space-y-3">
          <ProviderFields
            specs={account.account_credentials}
            values={values}
            onChange={setValues}
            stored={account.credentials}
            errors={fieldErrors}
            errorPrefix="credentials"
          />
          <Button
            size="sm"
            loading={save.isPending}
            disabled={!Object.values(values).some((v) => v.trim())}
            onClick={() => save.mutate()}
          >
            Save the login
          </Button>
        </CardBody>
      ) : null}
    </Card>
  );
}

function AccountSettings({ account }: { account: Account }) {
  const canEdit = useCan('messaging.provider_account.edit');
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    label: account.label,
    note: account.note ?? '',
    threshold: account.low_balance_threshold ?? '',
  });
  const save = useMutation({
    mutationFn: (body: object) => api.patch<AccountDetail>(`/messaging/provider-accounts/${account.public_id}`, body),
    onSuccess: (data) => {
      queryClient.setQueryData(keys.one(account.public_id), data);
      void queryClient.invalidateQueries({ queryKey: keys.list() });
      toast.success('Saved');
    },
    onError: (e) => toast.error('Not saved', refusalText(e)),
  });
  return (
    <Card>
      <CardHeader title="The account" />
      <CardBody className="space-y-4">
        <DetailGrid className="sm:grid-cols-2 lg:grid-cols-3">
          <DetailField label="Provider">{account.provider_label}</DetailField>
          <DetailField label="Vendor's reference">{account.account_ref}</DetailField>
          <DetailField label="Currency">{account.currency}</DetailField>
          <DetailField label="Low-balance alert">
            {account.low_balance_threshold ? formatMetered(account.low_balance_threshold) : 'Off'}
          </DetailField>
        </DetailGrid>
        {canEdit ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <Input label="Name" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
            <Input label="Note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            <Input
              label="Low-balance alert below (₹)"
              type="number"
              value={form.threshold}
              hint="Staff are told when a reading is under it. Empty: never."
              onChange={(e) => setForm({ ...form, threshold: e.target.value })}
            />
            <div className="flex flex-wrap gap-2 sm:col-span-2">
              <Button
                size="sm"
                loading={save.isPending}
                disabled={!form.label.trim()}
                onClick={() =>
                  save.mutate({
                    label: form.label.trim(),
                    note: form.note.trim() || null,
                    low_balance_threshold: form.threshold.trim() || null,
                  })
                }
              >
                Save
              </Button>
              <Button size="sm" variant="ghost" onClick={() => save.mutate({ is_active: !account.is_active })}>
                {account.is_active ? 'Switch off here' : 'Switch on here'}
              </Button>
            </div>
          </div>
        ) : account.note ? (
          <p className="text-sm text-muted">{account.note}</p>
        ) : null}
      </CardBody>
    </Card>
  );
}

function Clients({
  data,
  linkOps,
  onOpen,
  onDo,
}: {
  data: AccountDetail;
  linkOps: Operation[];
  onOpen: (id: string) => void;
  onDo: (op: Operation, connection: Connection) => void;
}) {
  const canRecord = useCan('messaging.provider_account.create');
  return (
    <div className="space-y-4">
      <Card className="overflow-hidden">
        <CardHeader title="Linked" description="Each client's account at the vendor. Open one to act on it." />
        {data.connections.length === 0 ? (
          <CardBody>
            <p className="text-sm text-muted">No client's service is linked to this account yet.</p>
          </CardBody>
        ) : (
          <Table>
            <thead className="bg-surface-2">
              <tr>
                <Th>Client</Th>
                <Th>Customer ID</Th>
                <Th>At the vendor</Th>
                <Th right>Balance (read)</Th>
                <Th right>Rate</Th>
                <Th right>Parts</Th>
                <Th>Billing</Th>
              </tr>
            </thead>
            <tbody>
              {data.connections.map((c) => (
                <tr
                  key={c.public_id}
                  className="cursor-pointer border-t border-line hover:bg-surface-2"
                  onClick={() => onOpen(c.public_id)}
                >
                  <td className="px-3 py-2 font-medium">
                    {c.client.name}
                    {c.label ? <span className="ml-1 text-xs text-muted">({c.label})</span> : null}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{c.customer_ref}</td>
                  <td className="px-3 py-2">
                    <StatusChip value={c.profile.active ?? null} labels={ACTIVE_LABELS} tones={ACTIVE_TONES} />
                  </td>
                  <td className="px-3 py-2 text-right tabular">
                    {c.balance ? formatMetered(c.balance) : '—'}
                    {c.balance_at ? <div className="text-2xs text-faint">{formatDateTime(c.balance_at)}</div> : null}
                  </td>
                  <td className="px-3 py-2 text-right tabular">
                    {c.profile.rate_per_part ? formatMetered(c.profile.rate_per_part) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right tabular">{c.parts === null ? '—' : formatNumber(c.parts)}</td>
                  <td className="px-3 py-2 text-xs text-muted">
                    {MODE_LABELS[c.billing_mode]}
                    {c.open_purchases ? (
                      <Badge tone="warning" className="ml-1">
                        {c.open_purchases} waiting
                      </Badge>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
      <Card className="overflow-hidden">
        <CardHeader
          title="Not linked yet"
          description="Services on this provider with no account here. Create the client's account at the vendor, or link the one it has."
        />
        {data.unlinked.length === 0 ? (
          <CardBody>
            <p className="text-sm text-muted">Every service on this provider is linked.</p>
          </CardBody>
        ) : (
          // Rows that wrap rather than a table: two actions beside a name do
          // not fit a column at a laptop's width, let alone a phone's.
          <ul className="divide-y divide-line border-t border-line">
            {data.unlinked.map((c) => (
              <li key={c.public_id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
                <div className="min-w-0">
                  <Link to={`/clients/${c.client.public_id}?tab=services`} className="font-medium hover:underline">
                    {c.client.name}
                  </Link>
                  {c.label ? <span className="ml-1 text-xs text-muted">({c.label})</span> : null}
                  <div className="text-xs text-muted">{MODE_LABELS[c.billing_mode]}</div>
                </div>
                {canRecord ? (
                  <div className="flex flex-wrap gap-2">
                    {linkOps.map((op) => (
                      <Button key={op.id} size="sm" variant="secondary" onClick={() => onDo(op, c)}>
                        {op.label}
                      </Button>
                    ))}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

const ACTION_ROUTES = {
  verify: { path: 'verify', permission: 'messaging.channel.edit' },
  sync_senders: { path: 'senders/sync', permission: 'messaging.sender.create' },
  sync_templates: { path: 'templates/sync', permission: 'messaging.template.create' },
} as const;

function ApiAction({ op, connection, account }: { op: Operation; connection: Connection; account: Account }) {
  // A connection's own actions have a route; a load (`account_load`) is
  // Connect's for each credit purchase and has no button.
  const route = op.action && op.action in ACTION_ROUTES ? ACTION_ROUTES[op.action as keyof typeof ACTION_ROUTES] : null;
  const allowed = useCan(route?.permission ?? 'messaging.channel.edit');
  const queryClient = useQueryClient();
  const run = useMutation({
    mutationFn: () => api.post(`/messaging/channels/${connection.public_id}/${route?.path ?? ''}`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.one(account.public_id) });
      toast.success(`${op.label}: done`);
    },
    onError: (e) => toast.error(`${op.label}: not done`, refusalText(e)),
  });
  if (!route) {
    return (
      <span className="text-xs text-muted">
        {op.label}: {op.description}
      </span>
    );
  }
  if (!allowed) return null;
  return (
    <Button size="sm" variant="secondary" loading={run.isPending} onClick={() => run.mutate()}>
      {op.label}
    </Button>
  );
}

function ConnectionDrawer({
  account,
  connection,
  ops,
  onClose,
  onDo,
}: {
  account: Account;
  connection: LinkedConnection;
  ops: Operation[];
  onClose: () => void;
  onDo: (op: Operation) => void;
}) {
  const canRecord = useCan('messaging.provider_account.create');
  const canEdit = useCan('messaging.provider_account.edit');
  const queryClient = useQueryClient();
  const unlink = useMutation({
    mutationFn: () =>
      api.delete<AccountDetail>(`/messaging/provider-accounts/${account.public_id}/connections/${connection.public_id}`),
    onSuccess: (data) => {
      queryClient.setQueryData(keys.one(account.public_id), data);
      toast.success('Unlinked');
      onClose();
    },
    onError: (e) => toast.error('Not unlinked', refusalText(e)),
  });
  const apiOps = ops.filter((o) => o.method === 'api');
  const panelOps = ops.filter((o) => o.method === 'panel');
  const profile = connection.profile;

  return (
    <Drawer open onClose={onClose} title={connection.client.name} width="lg">
      <div className="space-y-4">
        <Card>
          <CardHeader
            title={`Customer ${connection.customer_ref ?? ''}`}
            actions={<StatusChip value={profile.active ?? null} labels={ACTIVE_LABELS} tones={ACTIVE_TONES} />}
          />
          <CardBody>
            <DetailGrid className="sm:grid-cols-2 lg:grid-cols-2">
              <DetailField label="Balance (read from the vendor)">
                {connection.balance ? formatMetered(connection.balance) : null}
              </DetailField>
              <DetailField label="Read">{connection.balance_at ? formatDateTime(connection.balance_at) : null}</DetailField>
              <DetailField label="Rate plan">{profile.rate_plan}</DetailField>
              <DetailField label="Rate per part">{profile.rate_per_part ? formatMetered(profile.rate_per_part) : null}</DetailField>
              <DetailField label="Overdraft">{profile.overdraft ? formatMetered(profile.overdraft) : null}</DetailField>
              <DetailField label="Billing">{MODE_LABELS[connection.billing_mode]}</DetailField>
            </DetailGrid>
            {profile.recorded_at ? (
              <p className="mt-3 text-xs text-faint">
                What the panel says, as recorded by {profile.recorded_by || 'staff'} on {formatDateTime(profile.recorded_at)}.
                Never read back from the vendor.
              </p>
            ) : null}
          </CardBody>
        </Card>
        {connection.open_purchases ? (
          <Alert tone="warning" title={`${connection.open_purchases} credit purchase(s) waiting on a load`}>
            Load the account in the panel, then record it on the purchase in{' '}
            <Link to="/messaging/credit-purchases" className="underline">
              Credit purchases
            </Link>
            , where Connect proves it from the balance and adds the credits.
          </Alert>
        ) : null}
        {apiOps.length ? (
          <Card>
            <CardHeader title="Through the vendor's API" description="Connect does these itself." />
            <CardBody className="flex flex-wrap gap-2">
              {apiOps.map((op) => (
                <ApiAction key={op.id} op={op} connection={connection} account={account} />
              ))}
            </CardBody>
          </Card>
        ) : null}
        <Card>
          <CardHeader title="In the vendor's panel" description="The vendor has no API for these. Follow the steps there, then record the result." />
          <CardBody className="space-y-2">
            {panelOps.map((op) => (
              <div key={op.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-line pb-2 last:border-0">
                <div className="min-w-0">
                  <div className="text-sm font-medium">{op.label}</div>
                  <div className="text-xs text-muted">{op.description}</div>
                </div>
                {canRecord ? (
                  <Button size="sm" variant="secondary" onClick={() => onDo(op)}>
                    Steps and record
                  </Button>
                ) : null}
              </div>
            ))}
          </CardBody>
        </Card>
        <div className="flex flex-wrap gap-3">
          <Link to={`/clients/${connection.client.public_id}?tab=services`} className="text-sm text-accent hover:underline">
            The client's services
          </Link>
          {canEdit ? (
            <Button size="sm" variant="ghost" loading={unlink.isPending} onClick={() => unlink.mutate()}>
              Unlink from this account
            </Button>
          ) : null}
        </div>
      </div>
    </Drawer>
  );
}

function OperationDrawer({
  account,
  op,
  connection,
  openPurchases,
  onClose,
}: {
  account: Account;
  op: Operation;
  connection: Connection | null;
  openPurchases: number;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [values, setValues] = useState<FieldValues>(() => initialValues(op.record));
  const [note, setNote] = useState('');
  const save = useMutation({
    mutationFn: () =>
      api.post<{ operation: string; result: Record<string, string | null> }>(
        `/messaging/provider-accounts/${account.public_id}/operations`,
        {
          operation: op.id,
          connection: connection?.public_id ?? null,
          values: Object.fromEntries(Object.entries(values).filter(([, v]) => v.trim() !== '')),
          note: note.trim() || null,
        },
      ),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: keys.all });
      const after = data.result.balance_after;
      toast.success(
        'Recorded',
        after ? `The vendor now shows ${formatMetered(after)}.` : (data.result.error ?? undefined),
      );
      onClose();
    },
  });
  const fieldErrors = save.error instanceof ApiError ? save.error.fieldErrors : undefined;
  const blocked = op.effect === 'load' && connection?.billing_mode === 'credits' && openPurchases > 0;
  const panelHref = account.panel_url && op.panel_path ? `${account.panel_url}${op.panel_path}` : null;

  return (
    <Drawer open onClose={onClose} title={op.label} width="lg">
      <div className="space-y-4">
        <p className="text-sm text-muted">{op.description}</p>
        <Card>
          <CardHeader
            title={`In the vendor's panel${connection ? ` — ${connection.client.name}` : ''}`}
            actions={
              panelHref ? (
                <a
                  href={panelHref}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-accent hover:underline"
                >
                  Open the page <ExternalLink className="size-3.5" />
                </a>
              ) : null
            }
          />
          <CardBody>
            <ol className="list-decimal space-y-1.5 pl-5 text-sm">
              {op.steps.map((step) => (
                <li key={step}>{fillStep(step, connection?.client.name ?? null, connection?.customer_ref ?? null)}</li>
              ))}
            </ol>
          </CardBody>
        </Card>
        {blocked ? (
          <Alert tone="warning" title="Record this load on the purchase">
            This client has credit purchases waiting on a load. Record it in{' '}
            <Link to="/messaging/credit-purchases" className="underline">
              Credit purchases
            </Link>{' '}
            so it is counted once.
          </Alert>
        ) : (
          <Card>
            <CardHeader title="Record what was done" description="Kept in the log and the audit trail. Never a password." />
            <CardBody className="space-y-4">
              <ProviderFields specs={op.record} values={values} onChange={setValues} errors={fieldErrors} errorPrefix="values" />
              {Object.keys(op.sets).length ? (
                <p className="text-xs text-muted">
                  Records the account as {ACTIVE_LABELS[op.sets.active as 'yes' | 'no'] ?? ''} at the vendor.
                </p>
              ) : null}
              <Input label="Note" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
              {save.isError ? <Alert tone="danger">{refusalText(save.error) ?? 'Not recorded.'}</Alert> : null}
              <Button
                loading={save.isPending}
                disabled={!requiredFilled(op.record, values) || !fieldsValid(op.record, values)}
                onClick={() => save.mutate()}
              >
                Record it
              </Button>
            </CardBody>
          </Card>
        )}
      </div>
    </Drawer>
  );
}

function Log({ entries }: { entries: LogEntry[] }) {
  if (entries.length === 0) {
    return (
      <Card>
        <EmptyState title="Nothing recorded yet" description="What is done at the vendor and recorded here appears in this log." />
      </Card>
    );
  }
  return (
    <Card className="overflow-hidden">
      <Table>
        <thead className="bg-surface-2">
          <tr>
            <Th>When</Th>
            <Th>What</Th>
            <Th>Client</Th>
            <Th>Recorded</Th>
            <Th>By</Th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => (
            <tr key={e.public_id} className="border-t border-line align-top">
              <td className="whitespace-nowrap px-3 py-2 text-xs text-muted">{formatDateTime(e.at)}</td>
              <td className="px-3 py-2">{e.label}</td>
              <td className="px-3 py-2">{e.client ?? '—'}</td>
              <td className="px-3 py-2 text-xs">
                {Object.entries(e.values).map(([k, v]) => (
                  <div key={k}>
                    <span className="text-muted">{k.replaceAll('_', ' ')}:</span> {v}
                  </div>
                ))}
                {e.result.balance_after ? (
                  <div className="text-muted">balance read after: {formatMetered(e.result.balance_after)}</div>
                ) : null}
                {e.result.error ? <div className="text-danger">{e.result.error}</div> : null}
                {e.note ? <div className="text-muted">{e.note}</div> : null}
              </td>
              <td className="px-3 py-2 text-xs text-muted">{e.by}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}
